// Display formatters. fmtClock + fmtDur re-exported from @shared/time so
// they don't drift between web, cron and shared.

import { fmtClock, fmtDur } from '@shared/time.ts';

export { fmtClock, fmtDur };

export const fmtTime = (date: Date | number): string => {
  const d = typeof date === 'number' ? new Date(date * 1000) : date;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Past- or future-relative label ("just now" / "in 3m" / "5d ago"). */
export const relTime = (sec: number): string => {
  const diff = Math.floor(Date.now() / 1000) - sec;
  if (diff >= 0) {
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  }
  const ahead = -diff;
  if (ahead < 60) return 'in <1m';
  if (ahead < 3600) return `in ${Math.floor(ahead / 60)}m`;
  if (ahead < 86400) return `in ${Math.floor(ahead / 3600)}h`;
  return `in ${Math.floor(ahead / 86400)}d`;
};

/** Past-only label with minute precision past the hour ("5h 12m ago").
 *  Use when you want more detail than the rounded `relTime`. */
export const relAgo = (sec: number): string => {
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - sec);
  if (diff < 60) return 'just now';
  const mins = Math.floor(diff / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hours < 24) return rem ? `${hours}h ${rem}m ago` : `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

export const sinceDur = (sec: number): string => {
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - sec);
  if (diff < 60) return `${diff}s`;
  return fmtDur(Math.floor(diff / 60));
};

/** Age from a YYYY-MM-DD birth date. `short` returns "5w 2d", default
 *  returns "5 weeks · 2 days" for the long-form contexts. */
export const ageLabel = (isoDate: string, opts: { short?: boolean } = {}): string => {
  const [y, m, d] = isoDate.split('-').map(Number);
  const ms = Date.now() - new Date(y, m - 1, d).getTime();
  const days = Math.max(0, Math.floor(ms / 86_400_000));
  const weeks = Math.floor(days / 7);
  const remDays = days % 7;
  if (opts.short) {
    if (weeks < 1) return `${days}d`;
    return remDays ? `${weeks}w ${remDays}d` : `${weeks}w`;
  }
  if (weeks < 1) return `${days} day${days === 1 ? '' : 's'}`;
  return `${weeks} week${weeks === 1 ? '' : 's'} · ${remDays} day${remDays === 1 ? '' : 's'}`;
};
